import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { SelectField, TextField } from '@/components/ui/Field';
import { createVehicle } from '@/api/endpoints/vehicles';
import { queryKeys } from '@/api/queryKeys';
import { errorMessage } from '@/api/errors';

const VEHICLE_TYPES = ['Car', 'Motorcycle', 'Truck', 'Bus', 'Auto-rickshaw', 'Other'];

export function AddVehicleForm({ onAdded }: { onAdded?: () => void }) {
  const queryClient = useQueryClient();
  const [plate, setPlate] = useState('');
  const [type, setType] = useState(VEHICLE_TYPES[0] ?? 'Car');

  const mutation = useMutation({
    mutationFn: () => createVehicle({ license_plate: plate.trim().toUpperCase(), vehicle_type: type }),
    onSuccess: async () => {
      setPlate('');
      await queryClient.invalidateQueries({ queryKey: queryKeys.vehicles });
      await queryClient.invalidateQueries({ queryKey: queryKeys.summary });
      toast.success('Vehicle added.');
      onAdded?.();
    },
  });

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    mutation.mutate();
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <TextField
        label="Registration number"
        value={plate}
        onChange={(event) => setPlate(event.target.value.toUpperCase())}
        placeholder="KA 01 AB 1234"
        autoComplete="off"
        spellCheck={false}
        required
      />

      <SelectField label="Vehicle type" value={type} onChange={(event) => setType(event.target.value)}>
        {VEHICLE_TYPES.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </SelectField>

      {mutation.error && (
        <p className="rounded-[--radius-control] bg-bad-soft px-3 py-2 text-[13px] text-bad" role="alert">
          {errorMessage(mutation.error)}
        </p>
      )}

      <Button
        type="submit"
        variant="primary"
        loading={mutation.isPending}
        disabled={plate.trim().length === 0}
      >
        Add vehicle
      </Button>
    </form>
  );
}
